<template>
    <div class="audio-editor" @contextmenu.prevent>
        <Spinner v-if="loading" />
        <div class="audio-editor__player visually-hidden">
            <Player
                ref="player"
                no-save-time
                :src="bookpart.audio"
                @playing="playing = true"
                @paused="playing = false"
                @progress="onPlayerProgress"
                @finished="playing = false"
            />
        </div>
        <div class="audio-editor__inner-controls">
            <PlayButton
                :paused="playing"
                :class="['button', { 'is-active': playing }]"
                @click.prevent="togglePlayer"
            />
            <div class="audio-editor__zoom">
                <button
                    type="button"
                    :class="['button button--gray', { 'is-disabled': scale <= 1 }]"
                    :disabled="scale <= 1"
                    @click="zoom(-1)"
                >
                    <IconMinus />
                </button>
                <button
                    type="button"
                    :class="['button button--gray', { 'is-disabled': scale >= scaleMax }]"
                    :disabled="scale >= scaleMax"
                    @click="zoom(1)"
                >
                    <IconPlus />
                </button>
            </div>
        </div>
        <div class="audio-editor__waves-wrapper">
            <div
                ref="wavesContainer"
                class="audio-editor__waves-container"
                :style="{
                    width: `${waveContainerWidth * scale}px`,
                    height: `${waveContainerHeight}px`,
                }"
                @mousedown.left="handleMouseLeft"
                @mousedown.right="handleMouseRight"
            >
                <div
                    ref="playProgress"
                    class="audio-editor__progress"
                    :data-time="`${progressTime}s`"
                />
                <AudioEditorScale
                    :width="waveContainerWidth * scale"
                    :duration="duration"
                    :precision="precision"
                />
                <AudioEditorWaves
                    ref="wavesRes"
                    :url="bookpart.audio"
                    :height="waveContainerHeight"
                    :width="waveContainerWidth * scale"
                    :scale="scale"
                />
            </div>
            <div
                v-if="uncompletedSegment"
                ref="filler"
                :style="fillerStyles"
                :class="['audio-editor__filler', { 'audio-editor__filler--rtl': rtl }]"
                @mousedown.left="handleMouseLeft"
            >
                <span v-text="`${uncompletedSegment.time}s`" />
            </div>
            <div class="audio-editor__segments">
                <AudioEditorSegment
                    v-for="segment in segments"
                    :key="segment.id"
                    :height="waveContainerHeight"
                    :segment="segment"
                    :precision="precision"
                    :has-changes="hasChanges"
                    :offset-left="offsetLeft"
                    :selected-segment="selectedSegment"
                />
            </div>
        </div>

        <div v-if="tips.length" class="audio-editor__tips">
            <p v-for="(tip, i) in tips" :key="i" v-text="tip" />
        </div>

        <div v-if="!loading" class="audio-editor__actions">
            <template v-if="currentState === 'completed'">
                <button
                    v-t="'audioEditor.actions.selectSegment'"
                    type="button"
                    class="button button--outlined button--nowrap"
                    @click="setActiveSegment"
                />
            </template>
            <template v-else-if="selectedSegment">
                <template v-if="activeSegment.cut">
                    <button
                        v-t="'audioEditor.actions.uncutSegment'"
                        type="button"
                        class="button button--outlined"
                        @click="segmentUncut"
                    />
                </template>
                <template v-else>
                    <button
                        v-t="'audioEditor.actions.deleteSegment'"
                        type="button"
                        class="button button--outlined"
                        @click="segmentDelete"
                    />
                    <button
                        v-t="'audioEditor.actions.cutSegment'"
                        type="button"
                        class="button button--outlined"
                        @click="segmentCut"
                    />
                    <div class="audio-editor__cut">
                        <label v-t="'misc.speed'" />
                        <NumberInput v-model="segmentSpeed" step="0.05" :min="0.5" :max="4" />
                    </div>
                </template>
            </template>
        </div>
        <div v-if="!loading" class="audio-editor__bottom">
            <button
                v-t="'misc.save'"
                type="button"
                :class="['button button--big button--centered', { 'is-disabled': !canSave }]"
                :disabled="!canSave"
                @click="save"
            />
        </div>
    </div>
</template>

<script>
import AudioEditorSegment from './AudioEditorSegment';
import AudioEditorScale from './AudioEditorScale';
import AudioEditorWaves from './AudioEditorWaves';
import TRANSITIONS from './state/transitions';
import HANDLERS from './state/handlers';

import { PlayerSimple as Player, PlayButton } from '@speechki-frontend/shared/ui/Player';
import { IconPlus, IconMinus } from '@speechki-frontend/shared/ui/icons';
import { wrapInArray } from '@speechki-frontend/shared/helpers/functions';
import { NumberInput } from '@speechki-frontend/shared/ui/forms';
import throttle from 'lodash/throttle';

import { getAudioGenerationExtra } from './helpers/utils';

export default {
    name: 'AudioEditor',
    components: {
        AudioEditorSegment,
        AudioEditorScale,
        AudioEditorWaves,
        Player,
        PlayButton,
        IconPlus,
        IconMinus,
        NumberInput,
    },
    provide() {
        return {
            transition: this.transition,
            transitionIs: this.transitionIs,
        };
    },
    props: {
        bookpart: {
            type: Object,
            required: true,
        },
        scaleMax: {
            type: Number,
            default: 30,
        },
        rewindsLength: {
            type: Number,
            default: 500,
        },
        waveContainerHeight: {
            type: Number,
            default: 150,
        },
    },
    data() {
        return {
            currentState: 'idle',
            segments: [],
            scale: 1,
            precision: 0,
            basePrecision: 0,
            duration: 0,
            waveContainerWidth: 0,
            loading: false,
            playing: false,
            rtl: false,
            progressTime: 0,
            playerCurrentTime: 0,
            selectedSegment: null,
            segmentSpeed: 1.0,
            uncompletedSegment: undefined,
            lastCreatedSegment: undefined,
            throttledMouseHandler: throttle(this.handleMouseMove, 1000 / 60),
        };
    },
    computed: {
        hasChanges() {
            const { speed_chunks, trim_chunks } = getAudioGenerationExtra(this.bookpart);
            return !!(speed_chunks.length || trim_chunks.length);
        },
        observeMousePosition() {
            return (
                this.currentState !== 'completed' &&
                this.$refs.wavesContainer &&
                this.uncompletedSegment !== undefined
            );
        },
        canSave() {
            return this.segments.some(({ hasChange }) => hasChange);
        },
        offsetLeft() {
            const { wavesContainer } = this.$refs;
            if (!wavesContainer) return 0;
            return wavesContainer.getBoundingClientRect().left;
        },
        fillerStyles() {
            if (!this.uncompletedSegment) return {};

            let height = `${this.waveContainerHeight}px`;
            let { width, left } = this.uncompletedSegment;

            left = `${left}px`;
            width = `${width}px`;

            return { left, width, height };
        },
        tipParams() {
            const { start = 0, end = 0 } = this.activeSegment || {};
            const range = [start, end].map((i) => (i / 1000).toFixed(0));

            return {
                start: range[0],
                end: range[1],
            };
        },
        tips() {
            if (this.loading) return [];
            const prefix = 'audioEditor.tips';
            let path = this.currentState;

            if (!this.$te(`${prefix}.${path}`)) {
                path = 'idle';
            }

            const tips = this.$t(`${prefix}.${path}`, this.tipParams);
            return wrapInArray(tips);
        },
        activeSegment() {
            if (!this.selectedSegment) return {};
            return this.segments.find(({ id }) => id === this.selectedSegment);
        },
    },
    watch: {
        uncompletedSegment(newVal, oldVal) {
            if (newVal !== undefined || oldVal === undefined) return;
            this.lastCreatedSegment = oldVal.id;
        },
        segmentSpeed(speed) {
            const type = speed !== 1 ? 'speed' : 'none';
            this.updateActiveSegment({ type, speed });
        },
    },
    created() {
        this.transition({ type: 'FETCH' });
    },
    mounted() {
        window.addEventListener('mousemove', this.throttledMouseHandler, false);
        window.addEventListener('keyup', this.handleKeyUp);
    },
    beforeUnmount() {
        window.removeEventListener('mousemove', this.throttledMouseHandler, false);
        window.removeEventListener('keyup', this.handleKeyUp);
    },
    methods: {
        async command(nextState, action) {
            const handler = HANDLERS[nextState];
            await this.$nextTick();
            handler && handler.call(this, action);
        },
        transitionIs(state) {
            return this.currentState === state;
        },
        async transition(action) {
            const { [this.currentState]: actions = {} } = TRANSITIONS;
            const { type } = action;
            const { [type]: next } = actions;
            const node = `${this.currentState}/${type}`;

            if (!next) {
                console.log(`Invalid transition definition for state node '${node}'`);
                return;
            }

            // Set the new state BEFORE running the command handler: handlers
            // dispatch further transitions (e.g. `fetching` -> FETCHED) that read
            // `currentState`, so it must already reflect `next` when they run.
            this.currentState = next;
            await this.command(next, action);
        },
        async zoom(step) {
            this.scale += step;

            this.precision = this.basePrecision / this.scale;
            await this.$nextTick();
            this.loading = false;
        },

        togglePlayer() {
            this.$refs.player.toggle();
        },

        playerSetTime(time) {
            this.$refs.player.toTime(time / 1000);
            this.scrollToProgress();
        },

        playerSetCurrentTime(step) {
            const time = this.playerCurrentTime + Number(step);
            this.playerSetTime(time);
        },

        scrollToProgress() {
            const behavior = this.playing ? 'auto' : 'smooth';
            this.$refs.playProgress.scrollIntoView({ inline: 'center', behavior });
        },

        onPlayerProgress({ current, duration }) {
            this.playerCurrentTime = current * 1000;
            this.progressTime = current.toFixed(1);
            this.$refs.playProgress.style.left = (current / duration) * 100 + '%';
            this.scrollToProgress();
        },

        save() {
            this.transition({ type: 'SAVE' });
        },

        handleKeyUp({ keyCode }) {
            if (keyCode === 37) {
                this.playerSetCurrentTime(-this.rewindsLength);
            }
            if (keyCode === 39) {
                this.playerSetCurrentTime(this.rewindsLength);
            }
            if (keyCode === 32) {
                this.togglePlayer();
            }
        },

        handleMouseLeft(event) {
            if (this.hasChanges) {
                return false;
            }

            if (this.uncompletedSegment) {
                return this.segmentComplete(event);
            }

            if (this.uncompletedSegment) {
                return false;
            }

            this.segmentCreate(event);
        },

        handleMouseRight(event) {
            const finalEvent = event.$event ? event.$event : event;
            const left = finalEvent.pageX - finalEvent.target.getBoundingClientRect().left;
            let time = Math.round(left * this.precision);

            if (event.segment) {
                time = event.segment.start + time;
            }

            this.playerSetTime(time);
        },
        handleMouseMove(event) {
            if (!this.observeMousePosition) return;

            const { left, right } = this.$refs.wavesContainer.getBoundingClientRect();
            const { clientX: x, clientY: y, pageX } = event;
            const elFromPoint = document.elementFromPoint(x, y);

            if (
                pageX > right ||
                pageX < left ||
                elFromPoint.className.includes('audio-editor-segment')
            )
                return;

            //let left = pageX - bounding.left;
            let width = pageX - this.uncompletedSegment.left - left;

            this.rtl = width < 0;
            width += !this.rtl ? 2 : -2;

            this.uncompletedSegment['width'] = Math.abs(width);
        },
        addSegment(segment) {
            const newSegments = this.segments.slice();
            newSegments.push(segment);
            this.segments = newSegments;
        },
        updateSegmentDuration(e) {
            console.log(e);
        },
        segmentCreate(event) {
            this.transition({ type: 'SEGMENT_CREATE', event });
        },
        segmentComplete(event) {
            this.transition({ type: 'SEGMENT_COMPLETE', event });
        },
        segmentDelete(event) {
            this.transition({ type: 'SEGMENT_DELETE', event });
        },
        segmentCut(event) {
            this.transition({ type: 'SEGMENT_CUT', event });
        },
        segmentUncut(event) {
            const params = {
                type: 'none',
                cut: false,
            };
            this.transition({ type: 'SEGMENT_UNDO', event, params });
        },
        setActiveSegment(event) {
            const { lastCreatedSegment: id } = this;
            const params = { id };
            this.transition({ type: 'SET_ACTIVE', event, params });
        },
        updateSegment(segment, data) {
            const idx = this.segments.findIndex(({ id }) => id === segment);

            if (idx < 0) throw new Error(`Segment '${segment}' not found`);
            this.segments[idx] = { ...this.segments[idx], ...data, hasChange: true };
        },
        updateActiveSegment(data) {
            if (!this.selectedSegment) return;
            this.updateSegment(this.selectedSegment, data);
        },
    },
};
</script>

<style lang="scss" src="./AudioEditor.scss"></style>
